import { useContext, useState } from 'react'

import { AuthContext } from '../../context/AuthContext'
import { PASSWORD_HINT, passwordProblem, signUpErrorMessage } from './signUpRules'

import {
  Alert,
  Button,
  TextField,
  CircularProgress,
} from '@mui/material'

// Real (Cognito-backed) student account creation, gated behind guiSessionConfig's
// requireStudentAccount flag - see Login.jsx, which renders this in place of the guest-join
// button when the target session requires it. Unlike src/pages/SignUp.jsx (the existing
// College/CRN form, left untouched - its exact purpose is ambiguous, see the plan doc),
// this form is deliberately minimal: no College/CRN, since the session comes from the link
// the student was given, not a picker.
//
// On success, AuthContext.handleSignUp already calls setCurrentUser() with the account it
// just created, so Login.jsx's existing `currentUser` effect handles the post-auth redirect
// automatically - this component doesn't navigate itself.
const StudentSignUp = ({ disabled }) => {
  const { handleSignUp } = useContext(AuthContext)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [err, setErr] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const validateEmail = (value) => value.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErr('')

    if (!firstName || !lastName) {
      setErr('Please enter your first and last name')
      return
    }
    if (!email || !validateEmail(email)) {
      setErr('Please enter a valid email address')
      return
    }
    const problem = passwordProblem(password)
    if (problem) {
      setErr(problem)
      return
    }
    if (password !== confirmPassword) {
      setErr("Passwords don't match")
      return
    }

    setIsLoading(true)
    try {
      await handleSignUp({
        firstname: firstName,
        lastname: lastName,
        email,
        password,
        // Role 2 is TA (see role<=2 instructor/TA permission checks throughout the app) -
        // a real student account must not collide with that. Role 4 is dedicated to real,
        // self-registered student accounts.
        role: 4,
      })
    } catch (error) {
      console.error('Student sign up error:', error)
      setErr(signUpErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="login-form" noValidate>
      <label htmlFor="signup-first-name">First name</label>
      <TextField
        id="signup-first-name"
        variant="outlined"
        fullWidth
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        disabled={disabled || isLoading}
        autoComplete="given-name"
      />

      <label htmlFor="signup-last-name">Last name</label>
      <TextField
        id="signup-last-name"
        variant="outlined"
        fullWidth
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        disabled={disabled || isLoading}
        autoComplete="family-name"
      />

      <label htmlFor="signup-email">Email address</label>
      <TextField
        id="signup-email"
        type="email"
        placeholder="you@university.edu"
        variant="outlined"
        fullWidth
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={disabled || isLoading}
        autoComplete="email"
      />

      <label htmlFor="signup-password">Password</label>
      <TextField
        id="signup-password"
        type="password"
        variant="outlined"
        fullWidth
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        disabled={disabled || isLoading}
        autoComplete="new-password"
        helperText={PASSWORD_HINT}
      />

      <label htmlFor="signup-confirm-password">Confirm password</label>
      <TextField
        id="signup-confirm-password"
        type="password"
        variant="outlined"
        fullWidth
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        disabled={disabled || isLoading}
        autoComplete="new-password"
      />

      {err && <Alert severity="error" className="login-alert">{err}</Alert>}

      <Button className="login-submit" variant="contained" type="submit" fullWidth disabled={disabled || isLoading}>
        {isLoading ? <CircularProgress size={22} color="inherit" /> : <>Create account <span aria-hidden="true">→</span></>}
      </Button>
    </form>
  )
}

export default StudentSignUp
